import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-343" data-mp="30ndfkltewgk8">
      <div className="flex items-center justify-between" data-id="element-344" data-mp="30k920xu248gw">
        <label className="text-sm font-medium text-slate-600" data-id="element-345" data-mp="2mnlzmfjc4q3l">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-346" data-mp="2rm6wc4zdorna" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-347" data-mp="3jlt5m165pjkn" />
    </div>;
}
type InfoKey = 'master' | 'localization' | 'insights' | null;
interface InfoPopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  body: string;
}
function InfoPopover({
  isOpen,
  onToggle,
  onClose,
  title,
  body
}: InfoPopoverProps) {
  return <span className="relative inline-flex" data-id="element-348" data-mp="gvasb0c9rw3y">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={title} className="w-5 h-5 rounded-full bg-[#6C2BD9]/[0.08] hover:bg-[#6C2BD9]/[0.16] border border-[#6C2BD9]/30 text-[#6C2BD9] flex items-center justify-center transition-colors" data-id="element-349" data-mp="lrrhzyz4aij5">
        <Info className="w-3 h-3" data-id="element-350" data-mp="227avutw5c750" />
      </button>
      <AnimatePresence data-id="element-351" data-mp="228vo2cuzndzp">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15
      }} className="absolute z-30 top-full left-0 mt-2 w-[280px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-352" data-mp="267cu0t2oaifj">
            <div className="flex items-start justify-between gap-3" data-id="element-353" data-mp="32zt3ue32521q">
              <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-354" data-mp="2yyhbw1wpo4qd">
                {title}
              </p>
              <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-355" data-mp="2u1h3vlgawrwm">
                <X className="w-3.5 h-3.5" data-id="element-356" data-mp="s6glf6paa87h" />
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-357" data-mp="3l3t39hershkp">
              {body}
            </p>
          </motion.div>}
      </AnimatePresence>
    </span>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(10);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(4);
  const [countries, setCountries] = useState(10);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [unitEconomicsOpen, setUnitEconomicsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState<InfoKey>(null);
  // List-price unit economics — flat list prices
  const campaignDuration = 3;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  const effectiveMasterSnakCost = 3000;
  const masterSnakCost = effectiveMasterSnakCost;
  const effectiveCountryCost = 1000;
  const countryDeploymentCost = effectiveCountryCost;
  const effectiveMonthlyInsightsFee = 99;
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-358" data-mp="p5aefeijr034">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-359" data-mp="l3yyk0ph5t84">
        {/* Header */}
        <div className="mb-6" data-id="element-360" data-mp="g5dxjz2ef5im">
          <div className="flex items-center gap-3 mb-1" data-id="element-361" data-mp="g66uk8bk2gnk">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-362" data-mp="1cvei1lvqizu4">
              Snakker
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-363" data-mp="18uumunuq26am">
            See for yourself — move the sliders to see the{' '}
            <span className="text-[#6C2BD9]" data-id="element-364" data-mp="yvlt9w3rqtos">potential</span>
          </h1>
          <p className="text-xs md:text-sm text-slate-400 mt-2 max-w-3xl leading-relaxed" data-id="element-365" data-mp="yxpwebtufoo0">
            Big pharma typically runs 20–60 brands × 100–135 countries × 4–6
            campaigns/year. Fast Marketing
            <sup className="text-[0.7em] font-medium" data-id="element-366" data-mp="3jo7780s23a7i">™</sup> revenue per
            account scales directly with those three numbers.
          </p>

          {/* Industry benchmark popover */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-367" data-mp="1sfg6494r4ava">
            <div className="relative inline-block" data-id="element-368" data-mp="1dm36r1g9qw9y">
              <button type="button" onClick={() => setBenchmarkOpen(v => !v)} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-369" data-mp="1ijd37122s87a">
                <Info className="w-3.5 h-3.5" data-id="element-370" data-mp="21ihaa4a23rwb" />
                Industry benchmark
              </button>
              <AnimatePresence data-id="element-371" data-mp="2p8bnimyafih3">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-372" data-mp="2p5yv1fuwlpps">
                    <div className="flex items-start justify-between gap-3" data-id="element-373" data-mp="8n6j2ih0maa4">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-374" data-mp="4iyy8wdoheh6">
                        Big Pharma at a glance
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-375" data-mp="1fzq7tjh81ts3">
                        <X className="w-3.5 h-3.5" data-id="element-376" data-mp="9i8gzeheem7m" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-377" data-mp="4ivuwvycpj63">
                      Big pharma typically runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-378" data-mp="3vrjhff8bvcf8">
                        20–60 brands
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-379" data-mp="3mql94kquwiqu">
                        100–135 countries
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-380" data-mp="1kyqelhwqeuvu">
                        4–6 campaigns/year
                      </span>
                      .
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-381" data-mp="1sgy2sduicqpg">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-382" data-mp="1x86clazr2e35">
            <div className="rounded-2xl border border-slate-200 bg-white" data-id="element-383" data-mp="1x97unzf6o0ud">
              <button type="button" onClick={() => setUnitEconomicsOpen(v => !v)} aria-expanded={unitEconomicsOpen} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-384" data-mp="2267tpqdm83bz">
                <span className="flex items-center gap-2 min-w-0" data-id="element-385" data-mp="1ek1ae7yqr16g">
                  <SlidersHorizontal className="w-4 h-4 text-[#6C2BD9] flex-shrink-0" data-id="element-386" data-mp="1ikbgdkmj9rlb" />
                  <span className="text-sm font-semibold text-slate-700" data-id="element-387" data-mp="2pw23fz2n1bs0">
                    Unit economics
                  </span>
                  <span className="text-[11px] text-slate-400 hidden md:inline truncate" data-id="element-388" data-mp="2twvtaxzn50nj">
                    Fixed Snakker pricing behind the numbers
                  </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${unitEconomicsOpen ? 'rotate-180' : ''}`} data-id="element-389" data-mp="pukqzrsv1h1z" />
              </button>

              <AnimatePresence initial={false} data-id="element-390" data-mp="ywbrmi8jmxoz">
                {unitEconomicsOpen && <motion.div initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2
              }} className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200" data-id="element-391" data-mp="b6hlejiqc7dp">
                    {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-392" data-mp="1z2fir0mhfhnt">
                <div className="flex items-center justify-between" data-id="element-393" data-mp="23w08y6hu37cg">
                  <div className="flex items-center gap-2" data-id="element-394" data-mp="1q3tvwt9z9gh8">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-395" data-mp="1q17octn370z0">
                      Master Snak
                    </p>
                    <InfoPopover isOpen={infoOpen === 'master'} onToggle={() => setInfoOpen(infoOpen === 'master' ? null : 'master')} onClose={() => setInfoOpen(null)} title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-396" data-mp="2ir6fcukud86b" />
                  </div>
                  <div className="text-right" data-id="element-397" data-mp="1hn37xbnnx4j7">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-398" data-mp="1hnwjqr1hak87">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-399" data-mp="18sow5j2ccc4u">
                  Per campaign master creation
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-400" data-mp="20lpa6tux11op">
                  Flat list price. Follow-up campaigns reusing an existing
                  master are €1,000.
                </p>
              </div>

              {/* Cost per country localization */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-401" data-mp="1mm6g34yxvqve">
                <div className="flex items-center justify-between" data-id="element-402" data-mp="1moakdo0r2rw7">
                  <div className="flex items-center gap-2" data-id="element-403" data-mp="g6x6v69vq1d3">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-404" data-mp="b6hscbdv2ntj">
                      Cost per country localization
                    </p>
                    <InfoPopover isOpen={infoOpen === 'localization'} onToggle={() => setInfoOpen(infoOpen === 'localization' ? null : 'localization')} onClose={() => setInfoOpen(null)} title="What is Localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-405" data-mp="14xb78k1zzf8" />
                  </div>
                  <div className="text-right" data-id="element-406" data-mp="yquliioa4yg3">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-407" data-mp="ypjmew5s4evb">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-408" data-mp="30ir7km82pqp4">
                  One-time set-up per country
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-409" data-mp="2nigfvduiri4w">
                  Flat list price per country, per campaign.
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-410" data-mp="2xg36uyp3f058">
                <div className="flex items-center justify-between" data-id="element-411" data-mp="3bg4cergayvr1">
                  <div className="flex items-center gap-2" data-id="element-412" data-mp="3beazwvj0dhb4">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-413" data-mp="36g9maq4cqa71">
                      Data insights & hosting
                    </p>
                    <InfoPopover isOpen={infoOpen === 'insights'} onToggle={() => setInfoOpen(infoOpen === 'insights' ? null : 'insights')} onClose={() => setInfoOpen(null)} title="What are Data Insights?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper." data-id="element-414" data-mp="3l81nly7b9npr" />
                  </div>
                  <div className="text-right" data-id="element-415" data-mp="3pmbokckbp8v">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-416" data-mp="8ltjkioiratg">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-417" data-mp="3q0zzsyh69wi1">
                        / month
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-418" data-mp="3h0lbrrctq3au">Per country</p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-419" data-mp="3l49lafl0n8yq">
                  Flat monthly list price, billed per country for the{' '}
                  {campaignDuration}-month campaign run.
                </p>
              </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Portfolio Scale */}
            <div className="bg-[#6C2BD9]/[0.05] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-420" data-mp="3mmrppmdx43ky">
              <div data-id="element-421" data-mp="3ij3jb710ve1a">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-422" data-mp="3imhkibxy9h75">
                  Portfolio Scale
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-423" data-mp="38mqryfsy9c9p">
                  Move the sliders — revenue updates instantly.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-424" data-mp="2ywnoflo7cfx4" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={1} max={12} data-id="element-425" data-mp="2pvpqnl4cx4mz" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={140} data-id="element-426" data-mp="10dslouoywq0v" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-427" data-mp="1eed25k7eu9r7">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-428" data-mp="qnhi59974iui">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-429" data-mp="vhloprejuov2">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-430" data-mp="2y57e4y802q3">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-431" data-mp="3bhzf43b7a68d">
                per localized deployment
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-432" data-mp="3fi9gt3rymahf">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-433" data-mp="3kgt8lkt8ighp">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-434" data-mp="2rvjjgfqk3435">
                <div data-id="element-435" data-mp="2wtl24zk2l1e3">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-436" data-mp="2wt2wxdffjcu1">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-437" data-mp="31lw222et8n6n">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-438" data-mp="293qrmon83ox0">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-439" data-mp="292yu38a2fh43">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-440" data-mp="21jpvma33l2tt">
                    localized deployments / year
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-baseline justify-between" data-id="element-441" data-mp="1rl0bsbrn58ax">
                <p className="text-xs text-slate-500" data-id="element-442" data-mp="1nk6nhhaadrhi">
                  Estimated annual Snakker revenue
                </p>
                <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-443" data-mp="1ingaj2ts3c0b">
                  {formatEuro(calc.totalAnnualCost)}
                  <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-444" data-mp="18vak3xj6d2n8">
                    / year
                  </span>
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200" data-id="element-445" data-mp="zu3yja1o51n4">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-446" data-mp="uvj11idsww2d">
                Cost Breakdown
              </p>
              <div className="space-y-2" data-id="element-447" data-mp="l75dbwten3gu">
                <div className="flex justify-between items-center" data-id="element-448" data-mp="pajrsj1p3bs6">
                  <span className="text-sm text-slate-600" data-id="element-449" data-mp="p7p84tqi9uzt">
                    Avg. cost per localized campaign
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-450" data-mp="g8k9zoiuw3o6">
                    {formatEuro(calc.costPerDeployment)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-451" data-mp="18xrj1hfxsbno" />
                {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-452" data-mp="u9ehm4y2y0ls">
                    <span className="text-sm text-slate-500" data-id="element-453" data-mp="u70oi1mymygv">
                      New masters ({calc.firstCampaigns} ×{' '}
                      {formatEuro(masterSnakCost)})
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-454" data-mp="1mvz8z5ek2cpc">
                      {formatEuro(calc.firstCampaigns * masterSnakCost)}
                    </span>
                  </div>}
                {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-455" data-mp="37ervsfp1lj8z">
                    <span className="text-sm text-slate-500" data-id="element-456" data-mp="3l7rcgl0k9ji4">
                      Follow-up campaigns ({calc.followUpCampaigns} × €1,000)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-457" data-mp="3h7qwux1q729h">
                      {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                    </span>
                  </div>}
                <div className="flex justify-between items-center" data-id="element-458" data-mp="32hjesifmrl9b">
                  <span className="text-sm text-slate-500" data-id="element-459" data-mp="2xh5h9ubnntpj">
                    Localization & set-up
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-460" data-mp="hphhzs965p3k">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-461" data-mp="181uq0t6gs70f">
                  <span className="text-sm text-slate-500" data-id="element-462" data-mp="183pe2gd18f8q">
                    Hosting & data insights ({campaignDuration}mo)
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-463" data-mp="1hzitq8m1souq">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-4 border border-[#6C2BD9]/10" data-id="element-464" data-mp="1r072anso9x08">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-465" data-mp="1vxyz9w86c9ba">
                Not software pricing — this is annual revenue per account for a
                fully managed campaign operating model. ACV scales predictably
                with each brand, campaign and country added.
              </p>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-466" data-mp="2eq1dl0xcfdid">
              Land with a pilot brand in a few countries, expand across the
              portfolio. Same unit economics, compounding ACV.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-467" data-mp="3q7e231uai8e5"></div>
      </div>
    </div>;
}